import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Collapse,
  LinearProgress,
  useTheme,
  alpha,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { AccountBalanceWallet, CalendarMonth, CreditCard, LocalOffer, ExpandMore } from '@mui/icons-material';
import { useAuth } from '../../auth/AuthContext';
import { ensureWallet, getWalletSummary, topUpWallet, getWalletPermissions } from './walletService';

const WalletDialog = ({ open, onClose, tenantId }) => {
  const theme = useTheme();
  const { isAppAdmin, isSuperAdmin, currentUser, role, tenantId: authTenantId } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [walletPermissions, setWalletPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpError, setTopUpError] = useState('');
  const [topUpSuccess, setTopUpSuccess] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('Free');

  const effectiveTenantId = tenantId || authTenantId;
  const canView = isAppAdmin && !isSuperAdmin;

  const loadWallet = async () => {
    if (!open || !effectiveTenantId) return;
    setLoading(true);
    try {
      await ensureWallet(effectiveTenantId, { licenseType: 'Free' });
      const [summary, permissions] = await Promise.all([
        getWalletSummary(effectiveTenantId),
        getWalletPermissions(effectiveTenantId),
      ]);
      const walletData = summary?.wallet || null;
      setWallet(walletData);
      setTransactions(summary?.transactions || []);
      setWalletPermissions(permissions);
      setSelectedLicense(walletData?.licenseType || 'Free');
    } catch (error) {
      console.error('Unable to load wallet', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallet();
  }, [open, effectiveTenantId]);

  useEffect(() => {
    if (open) {
      setShowDetails(false);
    }
  }, [open]);

  const handleTopUp = async () => {
    setTopUpError('');
    setTopUpSuccess('');
    const value = Number(topUpAmount);

    if (!effectiveTenantId) {
      setTopUpError('No tenant is available for this wallet.');
      return;
    }

    if (!Number.isFinite(value) || value <= 0) {
      setTopUpError('Please enter a valid top-up amount.');
      return;
    }

    try {
      const result = await topUpWallet(effectiveTenantId, value, {
        description: 'Wallet top-up',
        actor: currentUser?.email || 'AppAdmin',
        performedBy: currentUser?.uid || null,
        performedByRole: role || 'AppAdmin',
        licenseType: selectedLicense,
        // expiryDate omitted — topUpWallet auto-extends by DEFAULT_EXPIRY_DAYS, see the code review doc
      });
      setWallet((prev) => prev ? { ...prev, ...result } : result);
      setTopUpAmount('');
      setTopUpSuccess(`Wallet topped up successfully. New balance: ${result.balance}`);
      await loadWallet();
    } catch (error) {
      setTopUpError(error?.message || 'Wallet top-up failed.');
    }
  };

  const latestTopUp = useMemo(() => {
    return transactions
      .filter((tx) => tx.type === 'topup')
      .sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      })[0] || null;
  }, [transactions]);

  const walletSummary = useMemo(() => {
    const balance = wallet?.balance ?? 0;
    const totalAllocated = wallet?.totalAllocated ?? balance;
    const used = Math.max(totalAllocated - balance, 0);
    const percentUsed = totalAllocated > 0 ? Math.min(Math.round((used / totalAllocated) * 100), 100) : 0;

    return {
      balance,
      totalAllocated,
      used,
      percentUsed,
      expiryDate: wallet?.expiryDate ? new Date(wallet.expiryDate.toDate ? wallet.expiryDate.toDate() : wallet.expiryDate).toLocaleDateString() : 'Not set',
      licenseType: wallet?.licenseType || 'Free',
    };
  }, [wallet]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary.main, 0.15)})` }}>
            <AccountBalanceWallet sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Box>
            <Typography variant="h6" component="div" sx={{ letterSpacing: 0.2 }}>Wallet</Typography>
            <Typography variant="body2" color="text.secondary">Quick wallet summary and controls</Typography>
          </Box>
        </Box>
        <Button variant="contained" color="secondary" size="small" onClick={() => setShowDetails((prev) => !prev)}>
          {showDetails ? 'Hide details' : 'View details'}
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {!canView ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">This wallet view is only available to tenant administrators.</Alert>
          </Box>
        ) : (
          <Box sx={{ p: 3, bgcolor: theme.palette.background.default }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 20px 40px rgba(20, 35, 72, 0.08)' }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Wallet overview</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    A clean summary of your wallet status. Open wallet details when you need to top up or review activity.
                  </Typography>

                  {/* Progress bar — the one addition beyond the original layout */}
                  <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Credits</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {walletSummary.used} used of {walletSummary.totalAllocated}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={walletSummary.percentUsed}
                    sx={{
                      mb: 3, height: 10, borderRadius: 5,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        transition: 'transform 500ms ease, width 500ms ease',
                      },
                    }}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Paper sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}` }}>
                      <Typography variant="caption" color="text.secondary">License</Typography>
                      <Typography variant="h6" fontWeight={700}>{walletSummary.licenseType}</Typography>
                    </Paper>
                    <Paper sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.secondary.main, 0.08), border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}` }}>
                      <Typography variant="caption" color="text.secondary">Balance</Typography>
                      <Typography variant="h6" fontWeight={700}>{walletSummary.balance}</Typography>
                    </Paper>
                    <Paper sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.divider, 1)}` }}>
                      <Typography variant="caption" color="text.secondary">Expiry</Typography>
                      <Typography variant="h6" fontWeight={700}>{walletSummary.expiryDate}</Typography>
                    </Paper>
                  </Stack>
                </Paper>

                <Collapse in={showDetails} timeout="auto" unmountOnExit>
                  <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 20px 40px rgba(20, 35, 72, 0.08)' }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Wallet details</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use your wallet to cover application operations and keep your balance up to date.
                    </Typography>
                    {walletPermissions && !walletPermissions.canAdd && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        {walletPermissions.reason || 'Wallet balance is insufficient. Add/update/delete actions are blocked. Read-only access only.'}
                      </Alert>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="wallet-license-label">License tier</InputLabel>
                        <Select
                          labelId="wallet-license-label"
                          value={selectedLicense}
                          label="License tier"
                          onChange={(event) => setSelectedLicense(event.target.value)}
                          sx={{ borderRadius: 3 }}
                        >
                          <MenuItem value="Free">Free</MenuItem>
                          <MenuItem value="Pro">Pro</MenuItem>
                          <MenuItem value="Enterprise">Enterprise</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Top-up amount"
                        type="number"
                        value={topUpAmount}
                        onChange={(event) => setTopUpAmount(event.target.value)}
                        fullWidth
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                      {topUpError && <Alert severity="error" sx={{ mt: 2 }}>{topUpError}</Alert>}
                      {topUpSuccess && <Alert severity="success" sx={{ mt: 2 }}>{topUpSuccess}</Alert>}
                      <Button variant="contained" color="primary" sx={{ mt: 2, borderRadius: 3 }} onClick={handleTopUp}>
                        Top up wallet
                      </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="subtitle2" fontWeight={700}>Last top-up</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Only the most recent top-up transaction is shown here.
                    </Typography>
                    {!latestTopUp ? (
                      <Alert severity="info">No top-up activity yet.</Alert>
                    ) : (
                      <Paper sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.grey[100], 0.8), border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>Top-up</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {latestTopUp.description || latestTopUp.appName || 'Wallet top-up'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {latestTopUp.createdAt?.toDate ? latestTopUp.createdAt.toDate().toLocaleString() : '—'}
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight={700} color={theme.palette.primary.main}>
                            {latestTopUp.amount ?? latestTopUp.balanceAfter ?? '-'}
                          </Typography>
                        </Stack>
                      </Paper>
                    )}
                  </Paper>
                </Collapse>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletDialog;
